import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "../services/axiosWrapper";
import { TOAST_TYPE_ERROR } from "../services/constants";
import { getFilePropertiesUrl } from "../services/dataManager";
import { showToastNotification } from "../reducers/toastSlice";
import { store } from "../ProviderApp";
import { QueryBuilder, executeQuery } from "../services/queryBuilder";
import {
  setDefaultView,
  setModelProperties,
  setSettingModalAPIData,
  setMeasurementAttributePopup,
} from "../reducers/Viewer";
import { setPortalUnits } from "../reducers/portalUnits";
import {
  setStaticDataForFile,
  setDynamicDataForFile,
  hasStaticDataForFile,
  markFileLoadState,
  getNodeModelPropertiesData,
  getModelPropertiesData,
} from "@Services/utils/modelPropertiesDataDB";
import {
  hasNodeIdAndUniqueIdMap,
  loadNodeIdAndUniqueIdMapIntoCache,
  setNodeIdAndUniqueIdMapForFile,
} from "@Services/utils/nodeIdAndUniqueIdMapDB";
import { runWithConcurrency } from "@Services/utils/helpers";
import { t } from "@i18n";
import { fetchDirectoryDataOfFile } from "./fileDirectoryData";
import type { GetViewsDataRequest, GetViewsDataResponse } from "@ServerTypes/views/views.schema";
import type {
  SaveConstituentFilesMatrixRequest,
  SaveConstituentFilesMatrixResponse,
} from "@ServerTypes/fileUpload/fileUpload.schema";
import type { UpdateFileUnitsRequest } from "@ServerTypes/queries/queries.schema";
import type { StatusResponse, ApiResponseWithData } from "@Helpers/schemas/common.schema.js";
import type { GetMeasurementUnitsResponse } from "@ServerTypes/units/units.schema";
import type { ViewerSetting, UpdateResponse as AccountsUpdateResponse } from "@ServerTypes/accounts/accounts.schema";
import type { Exact } from "@Helpers/types/exact";
import _ from "lodash";
import { unzip } from "@Services/utils/unarchive";
import { Matrix } from "@ts3d-hoops/web-viewer";
import { CubeGlobal } from "@Features/viewer-core";

export const selectionStages = {
  SELECTION_SET_LIST: "selectionSet_list",
  SELECTION_SET_ITEMS: "selectionSet_items",
} as const;

export interface PortalUnitEntry {
  unit: string;
  factor: number;
}

export interface PortalUnits {
  linearUnits: PortalUnitEntry[];
  areaUnits: PortalUnitEntry[];
  volumeUnits: PortalUnitEntry[];
}

export function saveModelTransform(data: Record<string, unknown>) {
  return () => {
    axiosWrapper.post<StatusResponse>(`${getBackendUrl()}/fileOperations/saveMetadataInFileVersion`, data);
  };
}

export async function getChildrenFilesName(data: { finalParentId: string; spaceId: string }) {
  return fetchDirectoryDataOfFile([
    { field: "parentId", value: data.finalParentId },
    { field: "spaceId", value: data.spaceId },
  ]);
}

export function getSettingModalData() {
  return async (dispatch: AppDispatch) => {
    const data = { projection: ["viewerSetting"] };
    const response = await axiosWrapper.post<ApiResponseWithData>(`${getBackendUrl()}/accounts/get`, data);
    dispatch(setSettingModalAPIData((response.data.data as Record<string, unknown>).viewerSetting));
  };
}

export async function updateSettingModalData(data: Partial<ViewerSetting>) {
  const response = await axiosWrapper.post<AccountsUpdateResponse>(`${getBackendUrl()}/accounts/update`, {
    viewerSetting: data,
  });
  return response;
}

export function shallowCompare<T extends Record<string, unknown>>(newObj: T, oldObj: T): Partial<T> {
  const differences: Partial<T> = {};
  const allKeys = new Set([...Object.keys(newObj), ...Object.keys(oldObj)]) as Set<keyof T>;
  for (const key of allKeys) {
    const value1 = newObj[key];
    const value2 = oldObj[key];

    if (value1 !== value2) {
      differences[key] = value1;
    }
  }

  return differences;
}

export async function fetchNodeIdAndUniqueIdMap(fileVersionId: string, signal?: AbortSignal) {
  const response = await axiosWrapper.get<ArrayBuffer>(
    `${getBackendUrl()}/fileSystemServer/View?fileVersionIds=${fileVersionId}&type=cubeUniqueIdAndNodeIdMapKey`,
    { responseType: "arraybuffer", signal },
  );

  if (!response.data?.byteLength) return null;

  const result = (await unzip(response.data, true, signal)) as Record<string, unknown>[];

  const merged: Record<string, string | number | boolean | null> = {};
  for (const obj of result) {
    const key = Object.keys(obj)[0];
    if (!key) {
      continue;
    }
    merged[key] = obj[key] as string | number | boolean | null;
  }

  return merged;
}

export async function getNodeIdAndUniqueIdMap(fileVersionId: string, signal?: AbortSignal) {
  try {
    // Already aborted before we started (e.g. queued behind other file loads).
    if (signal?.aborted) return;

    const exists = await hasNodeIdAndUniqueIdMap(fileVersionId);
    // Aborted during the existence check — stop before hydrating or fetching.
    if (signal?.aborted) return;

    if (exists) {
      await loadNodeIdAndUniqueIdMapIntoCache(fileVersionId);
      return;
    }

    const map = await fetchNodeIdAndUniqueIdMap(fileVersionId, signal);

    // Aborted mid-flight (viewer unmounted / file switched) — drop the result.
    if (signal?.aborted) return;

    if (map) {
      await setNodeIdAndUniqueIdMapForFile(fileVersionId, map);
    }
  } catch (err) {
    // Swallow the expected abort (canceled request / terminated unzip worker);
    // rethrow anything else so genuine failures still surface.
    if (signal?.aborted) return;
    throw err;
  }
}

type MeasurementCallback = ((...args: any[]) => void) | null | undefined;

type MeasurementStructure = Record<string, any> | null | undefined;

export function updateMeasurementAttributePopup(
  show: boolean,
  callback: MeasurementCallback,
  structure: MeasurementStructure,
) {
  return (dispatch: AppDispatch) => {
    dispatch(setMeasurementAttributePopup({ show, callback, structure }));
  };
}

export function getFaceSelectionItem(selectedItems: Array<{ _faceEntity: unknown }>) {
  for (let i = selectedItems.length - 1; i >= 0; i--) {
    if (selectedItems[i]!._faceEntity !== null) {
      return selectedItems[i];
    }
  }
  return null;
}

export function getModelFileFormTemplates(data: { fileVersionId: string[]; projectId: string }) {
  return async (dispatch: AppDispatch) => {
    const state = store.getState();
    const {
      modelProperties: { isFileFormTemplatesLoading, isFileFormTemplatesLoaded },
    } = state as Record<string, any>;
    if (!isFileFormTemplatesLoading && !isFileFormTemplatesLoaded) {
      dispatch(
        setModelProperties({
          isFileFormTemplatesLoading: true,
          isFileFormTemplatesLoaded: false,
        }),
      );

      let responseData: Record<string, any> = {};
      await Promise.all(
        data.fileVersionId.map(async (file: string) => {
          const response = await axiosWrapper.post<ApiResponseWithData>(
            `${getBackendUrl()}/projects/getModelFileFormTemplates`,
            {
              fileVersionId: file,
              projectId: data.projectId,
            },
          );
          if (response?.data?.status && (response.data?.data as Record<string, any>)?.defaultTemplate) {
            const questionIdAndNameMapper: Record<string, { question: any; type: any }> = {};

            for (const data of Object.values((response.data.data as Record<string, any>).formTemplates)) {
              for (const sections of Object.values(data as Record<string, any>)) {
                for (const question of (sections as Record<string, any>).questionFields) {
                  questionIdAndNameMapper[question._id] = {
                    question: question.questionValue,
                    type: question.fieldType,
                  };
                }
              }
            }

            responseData = {
              defaultTemplate: {
                ...responseData.defaultTemplate,
                ...(response.data.data as Record<string, any>).defaultTemplate,
              },
              formTemplates: {
                ...responseData.formTemplates,
                ...(response.data.data as Record<string, any>).formTemplates,
              },
              questionIdAndNameMapper: {
                ...responseData.questionIdAndNameMapper,
                ...questionIdAndNameMapper,
              },
            };
          } else {
            responseData = {
              defaultTemplate: responseData?.defaultTemplate || null,
              formTemplates: responseData?.formTemplates || null,
              questionIdAndNameMapper: responseData?.questionIdAndNameMapper || {},
            };
          }
        }),
      );
      dispatch(
        setModelProperties({
          ...responseData,
          isFileFormTemplatesLoading: false,
          isFileFormTemplatesLoaded: true,
        }),
      );
    }
  };
}

// export async function modelPropertiesResponse(data: any[], portalUnits?: any, unitsNew: any = {}) {
//   const responseData: any = {};
//   for (const fileVersionId of data) {
//     responseData[fileVersionId] = {};
//   }
//   await runWithConcurrency(
//     data.map((fileVersionId: any) => async () => {
//       const query = new Query().where("fileVersionId", "equal", [fileVersionId] as any);
//       const [mongoData, response] = await Promise.all([
//         getDataManagerObj(getFilePropertiesUrl).executeQuery(query),
//         axiosWrapper.get(`${getBackendUrl()}/fileSystemServer/View?fileVersionIds=${fileVersionId}&type=modelTreeKey`, {
//           responseType: "arraybuffer",
//         }),
//       ]);

//       if (response.data.byteLength) {
//         const result: any = await unzip(response.data);
//         const nodeIdDataMap: any = {};
//         for (const node of (mongoData as any).result) {
//           nodeIdDataMap[node._id] = node;
//         }
//         for (const node of result) {
//           const mappedNode = nodeIdDataMap[node._id];
//           if (mappedNode) {
//             const propertiesWithCubeData = {
//               ...node.properties,
//               Quantity: mappedNode.Quantity,
//               Unit: mappedNode.Unit,
//             };

//             if (Object.keys(unitsNew).length) {
//               node.properties = updateUnits(propertiesWithCubeData, unitsNew, portalUnits);
//               node.typeProperties = updateUnits(node.typeProperties, unitsNew, portalUnits);
//             }
//             node.customProperties = mappedNode.customProperties;
//             if (Object.hasOwn(mappedNode, "assetData")) {
//               node.assetData = mappedNode.assetData;
//             }
//             node.selectedFormTemplate = mappedNode.selectedFormTemplate;
//             responseData[fileVersionId][node.nodeId] = node;
//           }
//         }
//       } else if ((mongoData as any)?.result?.length) {
//         // Temp patch for demo
//         (mongoData as any)?.result.map((data: any) => {
//           responseData[fileVersionId][data.nodeId] = data;
//         });
//       }
//     }),
//   );
//   return responseData;
// }

// export function getModelProperties(data: any, unitsNew: any, portalUnits: any) {
//   return async (dispatch: AppDispatch) => {
//     const state = store.getState();
//     const {
//       modelProperties: { isLoading, isLoaded },
//     } = state as any;
//     if (!isLoading && !isLoaded) {
//       dispatch(
//         setModelProperties({
//           isLoading: true,
//           isLoaded: false,
//         }),
//       );
//       const responseData = await modelPropertiesResponse(data, portalUnits, unitsNew);
//       dispatch(
//         setModelProperties({
//           data: responseData,
//           isLoading: false,
//           isLoaded: true,
//         }),
//       );
//     }
//   };
// }

// ================= modelPropertiesResponse =================
export const modelPropertiesResponse = async (
  fileVersionIds: string[],
  estimateId: string | null = null,
  estimateVersionId: string | null = null,
  portalUnits?: PortalUnits,
  unitsNew: Record<string, unknown> = {},
  engineType?: string,
  returnData?: boolean,
  signal?: AbortSignal,
) => {
  // `processOneFileVersion` writes raw static + dynamic rows to IndexedDB
  // directly. Concurrency-cap the fetches so we don't open dozens of HTTP
  // streams against the same origin.
  await runWithConcurrency(
    fileVersionIds.map(
      (fileVersionId) => async () =>
        processOneFileVersion({
          fileVersionId,
          estimateId,
          estimateVersionId,
          signal,
        }),
    ),
    5,
    signal,
  );

  // Return the merged view for backward compat with callers that still
  // consume the response (QuerySettings, ClashTests/Rules, etc.). Merge
  // happens at read time inside `mergeNode` and applies the optional
  // unit conversion when `unitsNew` is supplied.

  const needToReturnData = returnData !== false;
  if (needToReturnData) {
    const responseData = await getModelPropertiesData({
      fileVersionId: fileVersionIds,
      unitsNew,
      portalUnits,
      engineType,
    });

    return responseData;
  }
};

// ================= getModelProperties =================
export function getModelProperties({
  fileVersionIds,
  unitsNew = {},
  estimateId = null,
  estimateVersionId = null,
  portalUnits = null,
  engineType,
  signal,
}: {
  fileVersionIds: string[];
  unitsNew?: Record<string, unknown>;
  estimateId?: string | null;
  estimateVersionId?: string | null;
  portalUnits?: PortalUnits | null;
  engineType: string;
  signal?: AbortSignal;
}) {
  return async (dispatch: AppDispatch) => {
    const state = store.getState();
    const { isLoading, isLoaded } = state.modelProperties || {};

    if (!isLoading && !isLoaded) {
      dispatch(setModelProperties({ isLoading: true, isLoaded: false }));

      try {
        // `modelPropertiesResponse` now writes static + dynamic rows to IndexedDB
        // directly via `processOneFileVersion`. The thunk just awaits completion
        // and bumps `dataVersion` so consumers re-read via the IDB layer.
        await modelPropertiesResponse(
          fileVersionIds,
          estimateId,
          estimateVersionId,
          portalUnits ?? undefined,
          unitsNew,
          engineType,
          false,
          signal,
        );

        if (signal?.aborted) {
          return;
        }

        dispatch(setModelProperties({ isLoading: false, isLoaded: true, bumpVersion: true }));
      } catch (err) {
        if ((err as DOMException)?.name === "AbortError" || signal?.aborted) {
          dispatch(setModelProperties({ isLoading: false, isLoaded: false }));
          return;
        }
        throw err;
      }
    }
  };
}

// ================= processOneFileVersion =================
async function processOneFileVersion({
  fileVersionId,
  estimateId = null,
  estimateVersionId = null,
  signal,
}: {
  fileVersionId: string;
  estimateId: string | null;
  estimateVersionId: string | null;
  signal?: AbortSignal;
}) {
  if (signal?.aborted) return;
  // 1. Static (AWS) data — fetch only when missing from IndexedDB. The model
  //    tree rarely changes for a given fileVersionId, so we persist it across
  //    sessions/tabs.

  let fileHasData = false;
  if (!(await hasStaticDataForFile(fileVersionId))) {
    if (signal?.aborted) return;
    const modelTreeResp = await fetchModelTreeZip(fileVersionId, signal);
    if (signal?.aborted) return;
    if (modelTreeResp.data?.byteLength) {
      fileHasData = true;
      // Pass the signal so an abort mid-load terminates the parse worker
      // instead of letting it run to completion.
      const staticRows = (await unzip(modelTreeResp.data, true, signal)) as Array<
        Record<string, any> & { nodeId: number | string; fileVersionId: string }
      >;
      if (signal?.aborted) return;
      await setStaticDataForFile(fileVersionId, staticRows, modelTreeResp.data.byteLength * 10);
    }
  } else {
    fileHasData = true;
  }

  if (signal?.aborted) return;
  // 2. Dynamic (Mongo) data — always refetch and replace; this layer holds
  //    custom metadata / quantity / asset data which can change frequently.
  const query = buildQuery({ fileVersionId, estimateId, estimateVersionId });
  const mongoData: any = await executeQuery(getFilePropertiesUrl, query, signal);
  if (signal?.aborted) return;
  const dynamicRows = (mongoData?.data ?? []).map((doc: Record<string, any>) => ({
    ...doc,
    fileVersionId,
  }));

  if (dynamicRows.length) {
    fileHasData = true;
  }

  await setDynamicDataForFile(fileVersionId, dynamicRows);

  // Files are processed one-by-one here, so record per-file whether it ended up
  // with rows. Lets a later external wipe (present → gone) be told apart from a
  // file that legitimately has no model data (e.g. a CAD file with no tree/docs).
  // Drives `useModelPropertiesWiped`.
  fileHasData && markFileLoadState(fileVersionId, true);
}

function buildQuery({
  fileVersionId,
  estimateId,
  estimateVersionId,
}: {
  fileVersionId: string;
  estimateId: string | null;
  estimateVersionId: string | null;
}) {
  const query = new QueryBuilder().where("fileVersionId", "equal", fileVersionId);

  if (estimateId?.length) {
    query.where("estimateId", "equal", estimateId);
  }

  if (estimateVersionId?.length) {
    query.where("estimateVersionId", "equal", estimateVersionId);
  }

  return query;
}

function fetchModelTreeZip(fileVersionId: string, signal?: AbortSignal) {
  return axiosWrapper.get<ArrayBuffer>(
    `${getBackendUrl()}/fileSystemServer/View?fileVersionIds=${fileVersionId}&type=modelTreeKey`,
    { responseType: "arraybuffer", signal },
  );
}

export const saveConstituentMatrix = async <T extends Exact<SaveConstituentFilesMatrixRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<SaveConstituentFilesMatrixResponse>(
    `${getBackendUrl()}/fileUpload/createConstituentFilesMatrix`,
    data,
  );
  return response;
};

interface Coordinates3D {
  x: number;
  y: number;
  z: number;
}
interface OrientationData {
  nodeId: number;
  data: {
    scale: { coordinates: Coordinates3D };
    rotation: { coordinates: Coordinates3D };
    position: { coordinates: Coordinates3D };
  };
}

export function changeOrientation(data: OrientationData) {
  const matrix = CubeGlobal.hwv.model.getNodeMatrix(data.nodeId);
  let initialMatrix = matrix.loadIdentity();

  const treeNode = CubeGlobal.hwv._modelStructure._assemblyTree.lookupAnyTreeNode(data.nodeId);
  if (treeNode) {
    const nodeMatrix = treeNode._localTransform;
    if (nodeMatrix) {
      initialMatrix = Matrix.createFromArray(nodeMatrix);
    }
  }

  let demoMatrix = matrix.loadIdentity();

  demoMatrix.setScaleComponent(
    data.data.scale.coordinates.x,
    data.data.scale.coordinates.y,
    data.data.scale.coordinates.z,
  ); // Scalaing the matrix

  const xmat = Matrix.xAxisRotation(data.data.rotation.coordinates.x);
  const ymat = Matrix.yAxisRotation(data.data.rotation.coordinates.y);
  const zmat = Matrix.zAxisRotation(data.data.rotation.coordinates.z);
  const xymat = Matrix.multiply(ymat, xmat);
  const xyzmat = Matrix.multiply(zmat, xymat);
  demoMatrix = Matrix.multiply(xyzmat, demoMatrix);
  demoMatrix.setTranslationComponent(
    data.data.position.coordinates.x,
    data.data.position.coordinates.y,
    data.data.position.coordinates.z,
  );

  const finalMatrix = Matrix.multiply(initialMatrix, demoMatrix);
  CubeGlobal.hwv.model.setNodeMatrix(data.nodeId, finalMatrix);
}

export const downloadAndunzip = async (awsKey: string, isJson = true) => {
  const response = await axiosWrapper.get<ArrayBuffer>(
    `${getBackendUrl()}/fileSystemServer/Download?viewId=${awsKey}`,
    {
      responseType: "arraybuffer",
    },
  );
  const result = await unzip(response.data, isJson);
  return result;
};

/**
 * Fetches views data (saved camera positions) for a specific file.
 * Uses shared types from helpers for type-safe request/response.
 *
 * @param params - Request parameters
 * @param params.fileId - The file ID to get views for
 * @param params.type - 'custom' for personal views, 'shared' for shared views
 * @returns Promise with typed response containing view records
 */
export const getViewsData = async <T extends Exact<GetViewsDataRequest, T>>({
  fileId,
  type,
}: T): Promise<{ data: GetViewsDataResponse }> => {
  const response = await axiosWrapper.post<GetViewsDataResponse>(`${getBackendUrl()}/views/getViewsData`, {
    fileId,
    type,
  });
  return response;
};

export const getDefaultView = (data: { fileId: string }) => {
  return async (dispatch: AppDispatch) => {
    const responseData = await axiosWrapper.post<ApiResponseWithData>(`${getBackendUrl()}/views/getDefaultView`, {
      fileId: data.fileId,
    });

    let defaultView: Record<string, unknown> | null = null;
    if (responseData.data.status) {
      const viewPointS3Key = (responseData.data.data as Record<string, any>[])[0]!.viewpoint;
      if (viewPointS3Key) {
        const defaultViewPoint = await downloadAndunzip(viewPointS3Key);
        const viewData = defaultViewPoint;
        defaultView = {
          _id: (responseData.data.data as Record<string, any>[])[0]!._id,
          viewpoint: viewData,
        };
      }
    }

    dispatch(setDefaultView(defaultView));
  };
};

export const updateFileUnits = async <T extends Exact<UpdateFileUnitsRequest, T>>(data: T) => {
  const result = await axiosWrapper.post<StatusResponse>(`${getBackendUrl()}/queries/updateFileUnits`, data);
  return result.data;
};

export const fetchUnits = () => async (dispatch: AppDispatch) => {
  const [linearRes, areaRes, volumeRes] = await Promise.all([
    axiosWrapper.post<GetMeasurementUnitsResponse>(`${getBackendUrl()}/units/getMeasurementUnits`, {
      value: { params: { type: "Linear" }, type: "Linear" },
    }),
    axiosWrapper.post<GetMeasurementUnitsResponse>(`${getBackendUrl()}/units/getMeasurementUnits`, {
      value: { params: { type: "Area" }, type: "Area" },
    }),
    axiosWrapper.post<GetMeasurementUnitsResponse>(`${getBackendUrl()}/units/getMeasurementUnits`, {
      value: { params: { type: "Volume" }, type: "Volume" },
    }),
  ]);

  type UnitRecord = { nameWithSymbol: string; factor: number };
  const toUnitRecords = (units: UnitRecord[] | undefined) =>
    (units ?? []).map((unit) => ({ unit: unit.nameWithSymbol, factor: unit.factor }));

  dispatch(
    setPortalUnits({
      linearUnits: toUnitRecords(linearRes.data.result as UnitRecord[]),
      areaUnits: toUnitRecords(areaRes.data.result as UnitRecord[]),
      volumeUnits: toUnitRecords(volumeRes.data.result as UnitRecord[]),
    }),
  );
};

export function dispatchModelLoadingToast() {
  return (dispatch: AppDispatch) => {
    if (window.CubeGlobal?.socketDisconnected) {
      dispatch(dispatchModelDisconnectedToast());
      return;
    }
    dispatch(
      showToastNotification({
        content: t("Please Wait for Model to Load"),
        type: TOAST_TYPE_ERROR,
      }),
    );
  };
}

export function dispatchModelDisconnectedToast() {
  return (dispatch: AppDispatch) => {
    dispatch(
      showToastNotification({
        content: t("modelLoadConnectionLost"),
        type: TOAST_TYPE_ERROR,
        timeOut: 8000,
      }),
    );
  };
}

export const createAssetDataPayload = async ({ selectionData }: { selectionData: Record<string, string[]> }) => {
  const payload: Record<string, unknown>[] = [];
  for (const fileVersionId in selectionData) {
    const nodeIds = selectionData[fileVersionId]!;
    for (const nodeId of nodeIds) {
      const meta = (await getNodeModelPropertiesData(fileVersionId, nodeId)) ?? {};
      payload.push({
        fileVersionId,
        nodeId,
        childrenMap: meta.childrenMap,
        parentMap: meta.parentMap,
        parent: meta.parent,
        projectId: meta.projectId,
        cubeUniqueId: meta.cubeUniqueId,
        portalId: meta.portalId,
        fileId: meta.fileId,
      });
    }
  }

  return payload;
};
